import { getId } from '@mimir/client-common/model/ongoingDownload';
import type { OngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import type { MonitoredTransfer } from '@mimir/common/dto/transferRequestDtos';
import { vueDelete, vueSet } from '../compat/vueCompat';
import type { Subclip } from '@mimir/client-common/model/subclip';
import uniq from 'lodash/uniq';
import { showToast, TOAST } from '@mimir/web/eventBus';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import { transfer } from '../globals/transfer';

export class DefaultOngoingDownloadOps implements OngoingDownloadOps {

  getOngoingDownloadKeys(): string[] {
    return Object.keys(transfer.ongoingDownloads);
  }

  getOngoingDownload(odId: string): OngoingDownload | null {
    return transfer.ongoingDownloads[odId] ?? null;
  }

  getOngoingDownloadById(id: string): OngoingDownload | null {
    return Object.values(transfer.ongoingDownloads).find(od => od.id === id) ?? null;
  }

  addOrUpdateOngoingDownload(newOngoingDownload: OngoingDownload): void {
    const odId = getId(newOngoingDownload);
    const ongoingDownload = transfer.ongoingDownloads[odId];
    let addedSubclip: Subclip | null = null;

    if (ongoingDownload) {
      const reporters = uniq([...(ongoingDownload.reporters ?? []), ...(newOngoingDownload.reporters ?? [])]);
      const deliver = uniq([...(ongoingDownload.deliver ?? []), ...(newOngoingDownload.deliver ?? [])]);

      const mergedOngoingDownload = {
        ...ongoingDownload,
        ...newOngoingDownload,
        reporters,
        deliver,
      };

      vueSet(transfer.ongoingDownloads, odId, mergedOngoingDownload);
    } else {
      addedSubclip = newOngoingDownload.subclip ?? null;
      vueSet(transfer.ongoingDownloads, odId, newOngoingDownload);
    }

    if (addedSubclip !== null) {
      const rangeList = `${addedSubclip.startTime}-${addedSubclip.endTime}`;
      showToast(`Subclip queued (${rangeList})`, TOAST.SUCCESS);
    }
  }

  deleteFromOngoingDownloads(odId: string): void {
    const ongoingDownload = transfer.ongoingDownloads[odId];
    if (ongoingDownload) {
      vueDelete(transfer.ongoingDownloads, odId);
    }
  }

  updateOngoingDownload(ongoingDownload: OngoingDownload): void {
    const odId = getId(ongoingDownload);
    const existingOngoingDownload = transfer.ongoingDownloads[odId];
    if (existingOngoingDownload) {
      this.addOrUpdateOngoingDownload(ongoingDownload);
    }
  }

  clearOngoingDownloads(): void {
    transfer.ongoingDownloads = {};
  }

  mapDownloadProgress(transfers: MonitoredTransfer[]): void {
    for (const t of transfers) {
      // Use id instead of odId to find the entry because that is more reliable for external transfers.
      const ongoingDownload = this.getOngoingDownloadById(t.id);
      if (!ongoingDownload) {
        // The transfer was not added from this client instance. Ignore.
        continue;
      }

      vueSet(ongoingDownload, 'downloadProgress', t.downloadProgress);
      vueSet(ongoingDownload, 'transferState', t.transferState);
      ongoingDownload.highresIsOffline = t.highresIsOffline;

      const downloadProgressDto = t.downloadProgress;
      if (downloadProgressDto) {
        const percentage = 100 * downloadProgressDto.transferred / downloadProgressDto.totalSize;
        for (const reporter of ongoingDownload.reporters ?? []) {
          reporter.onProgress(percentage);
        }
      }
    }
  }

}
